import {IAction, IAppError, RequestState} from "@pg-mono/request-state";

import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IVendorDetailApplication} from "../../vendor/types/IVendorDetailApplication";
import {postApplicationTypes} from "../actions/after_post_application";
import {getApplicationOfferDetailTypes} from "../actions/get_application_offer_detail";
import {getApplicationPropertyDetailTypes} from "../actions/get_application_property_detail";
import {getApplicationRelatedDataTypes} from "../actions/get_application_related_data";
import {getApplicationVendorDetailTypes} from "../actions/get_application_vendor_detail";
import {
    appendMultiLeadApplicationsTypes,
    IMultiLeadApplication,
    IMultiLeadApplicationsActions,
    IMultiLeadApplicationsAppendAction
} from "../actions/multi_lead/append_multi_lead_applications";
import {IApplicationData} from "../types/IApplicationData";

export interface IApplicationStore {
    [storeHash: string]: IApplicationReducerData | undefined;
}

export interface IApplicationReducerData {
    request: RequestState;
    application: IApplicationData | null;
    error: IAppError | null;
    vendor: IAdditionalData<IVendorDetailApplication> | null;
    offer: IAdditionalData<IOfferDetailApplication> | null;
    property: IAdditionalData<IPropertyDetailApplication> | null;
    leadDataRequest: RequestState;
    multiLeadApplications: IMultiLeadApplication[] | null;
}

export interface IAdditionalData<T> {
    request: RequestState;
    detail?: T;
    error?: IAppError;
}

interface IApplicationActionStart extends IAction {
    id: string;
}

interface IApplicationActionSuccess extends IAction {
    id: string;
    result: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface IApplicationActionError extends IAction {
    id: string;
    error: IAppError;
}

interface IApplicationActionReset extends IAction {
    id: string;
}

export type IApplicationAction =
    | IApplicationActionStart
    | IApplicationActionSuccess
    | IApplicationActionError
    | IApplicationActionReset
    | IMultiLeadApplicationsActions;

const getInitialApplicationData = (): IApplicationReducerData => ({
    request: RequestState.None,
    application: null,
    error: null,
    vendor: null,
    offer: null,
    property: null,
    leadDataRequest: RequestState.None,
    multiLeadApplications: null
});

export function applicationReducer(state: IApplicationStore = {}, action: IApplicationAction): IApplicationStore {
    const actionSuccess = action as IApplicationActionSuccess;
    const actionError = action as IApplicationActionError;
    const currentData: IApplicationReducerData = state[action.id] == null ? getInitialApplicationData() : (state[action.id] as IApplicationReducerData);

    switch (action.type) {
        /**
         *  CREATE
         */

        case postApplicationTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.Waiting
                }
            };
        case postApplicationTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.Success,
                    application: actionSuccess.result
                }
            };
        case postApplicationTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.Error,
                    error: actionError.error
                }
            };
        case postApplicationTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.None,
                    error: null
                }
            };

        /**
         * Default state
         */
        case getApplicationRelatedDataTypes.initialize:
            return {
                ...state,
                [action.id]: getInitialApplicationData()
            };

        /**
         * Lead data
         */
        case getApplicationRelatedDataTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    leadDataRequest: RequestState.Waiting
                }
            };
        case getApplicationRelatedDataTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    leadDataRequest: RequestState.Success
                }
            };
        case getApplicationRelatedDataTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    leadDataRequest: RequestState.Error
                }
            };

        /**
         *  VENDOR
         */

        case getApplicationVendorDetailTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: {
                        request: RequestState.Waiting
                    }
                }
            };
        case getApplicationVendorDetailTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: {
                        request: RequestState.Success,
                        detail: actionSuccess.result
                    }
                }
            };
        case getApplicationVendorDetailTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: {
                        request: RequestState.Error,
                        error: actionError.error
                    }
                }
            };
        case getApplicationVendorDetailTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: null
                }
            };

        /**
         *  OFFER
         */

        case getApplicationOfferDetailTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: {
                        request: RequestState.Waiting
                    }
                }
            };
        case getApplicationOfferDetailTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: {
                        request: RequestState.Success,
                        detail: actionSuccess.result
                    }
                }
            };
        case getApplicationOfferDetailTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: {
                        request: RequestState.Error,
                        error: actionError.error
                    }
                }
            };
        case getApplicationOfferDetailTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: null
                }
            };

        /**
         *  PROPERTY
         */

        case getApplicationPropertyDetailTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: {
                        request: RequestState.Waiting
                    }
                }
            };
        case getApplicationPropertyDetailTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: {
                        request: RequestState.Success,
                        detail: actionSuccess.result
                    }
                }
            };
        case getApplicationPropertyDetailTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: {
                        request: RequestState.Error,
                        error: actionError.error
                    }
                }
            };
        case getApplicationPropertyDetailTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: null
                }
            };

        /**
         * multi lead applications
         */
        case appendMultiLeadApplicationsTypes.append:
            const multiLeadAction = action as IMultiLeadApplicationsAppendAction;

            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    multiLeadApplications: [...(currentData.multiLeadApplications || []), ...multiLeadAction.applications]
                }
            };
        case appendMultiLeadApplicationsTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    multiLeadApplications: []
                }
            };

        default:
            return state;
    }
}
import {IAction, IAppError, RequestState} from "@pg-mono/request-state";

import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {IVendorDetailApplication} from "../../vendor/types/IVendorDetailApplication";
import {postApplicationTypes} from "../actions/after_post_application";
import {getApplicationOfferDetailTypes} from "../actions/get_application_offer_detail";
import {getApplicationPropertyDetailTypes} from "../actions/get_application_property_detail";
import {getApplicationRelatedDataTypes} from "../actions/get_application_related_data";
import {getApplicationVendorDetailTypes} from "../actions/get_application_vendor_detail";
import {
    appendMultiLeadApplicationsTypes,
    IMultiLeadApplication,
    IMultiLeadApplicationsActions,
    IMultiLeadApplicationsAppendAction
} from "../actions/multi_lead/append_multi_lead_applications";
import {IApplicationData} from "../types/IApplicationData";

export interface IApplicationStore {
    [storeHash: string]: IApplicationReducerData | undefined;
}

export interface IApplicationReducerData {
    request: RequestState;
    application: IApplicationData | null;
    error: IAppError | null;
    vendor: IAdditionalData<IVendorDetailApplication> | null;
    offer: IAdditionalData<IOfferDetailApplication> | null;
    property: IAdditionalData<IPropertyDetailApplication> | null;
    leadDataRequest: RequestState;
    multiLeadApplications: IMultiLeadApplication[] | null;
}

export interface IAdditionalData<T> {
    request: RequestState;
    detail?: T;
    error?: IAppError;
}

interface IApplicationActionStart extends IAction {
    id: string;
}

interface IApplicationActionSuccess extends IAction {
    id: string;
    result: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface IApplicationActionError extends IAction {
    id: string;
    error: IAppError;
}

interface IApplicationActionReset extends IAction {
    id: string;
}

export type IApplicationAction =
    | IApplicationActionStart
    | IApplicationActionSuccess
    | IApplicationActionError
    | IApplicationActionReset
    | IMultiLeadApplicationsActions;

const getInitialApplicationData = (): IApplicationReducerData => ({
    request: RequestState.None,
    application: null,
    error: null,
    vendor: null,
    offer: null,
    property: null,
    leadDataRequest: RequestState.None,
    multiLeadApplications: null
});

export function applicationReducer(state: IApplicationStore = {}, action: IApplicationAction): IApplicationStore {
    const actionSuccess = action as IApplicationActionSuccess;
    const actionError = action as IApplicationActionError;
    const currentData: IApplicationReducerData = state[action.id] == null ? getInitialApplicationData() : (state[action.id] as IApplicationReducerData);

    switch (action.type) {
        /**
         *  CREATE
         */

        case postApplicationTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.Waiting
                }
            };
        case postApplicationTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.Success,
                    application: actionSuccess.result
                }
            };
        case postApplicationTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.Error,
                    error: actionError.error
                }
            };
        case postApplicationTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    request: RequestState.None,
                    error: null
                }
            };

        /**
         * Default state
         */
        case getApplicationRelatedDataTypes.initialize:
            return {
                ...state,
                [action.id]: getInitialApplicationData()
            };

        /**
         * Lead data
         */
        case getApplicationRelatedDataTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    leadDataRequest: RequestState.Waiting
                }
            };
        case getApplicationRelatedDataTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    leadDataRequest: RequestState.Success
                }
            };
        case getApplicationRelatedDataTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    leadDataRequest: RequestState.Error
                }
            };

        /**
         *  VENDOR
         */

        case getApplicationVendorDetailTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: {
                        request: RequestState.Waiting
                    }
                }
            };
        case getApplicationVendorDetailTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: {
                        request: RequestState.Success,
                        detail: actionSuccess.result
                    }
                }
            };
        case getApplicationVendorDetailTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: {
                        request: RequestState.Error,
                        error: actionError.error
                    }
                }
            };
        case getApplicationVendorDetailTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    vendor: null
                }
            };

        /**
         *  OFFER
         */

        case getApplicationOfferDetailTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: {
                        request: RequestState.Waiting
                    }
                }
            };
        case getApplicationOfferDetailTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: {
                        request: RequestState.Success,
                        detail: actionSuccess.result
                    }
                }
            };
        case getApplicationOfferDetailTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: {
                        request: RequestState.Error,
                        error: actionError.error
                    }
                }
            };
        case getApplicationOfferDetailTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    offer: null
                }
            };

        /**
         *  PROPERTY
         */

        case getApplicationPropertyDetailTypes.start:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: {
                        request: RequestState.Waiting
                    }
                }
            };
        case getApplicationPropertyDetailTypes.success:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: {
                        request: RequestState.Success,
                        detail: actionSuccess.result
                    }
                }
            };
        case getApplicationPropertyDetailTypes.error:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: {
                        request: RequestState.Error,
                        error: actionError.error
                    }
                }
            };
        case getApplicationPropertyDetailTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    property: null
                }
            };

        /**
         * multi lead applications
         */
        case appendMultiLeadApplicationsTypes.append:
            const multiLeadAction = action as IMultiLeadApplicationsAppendAction;

            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    multiLeadApplications: [...(currentData.multiLeadApplications || []), ...multiLeadAction.applications]
                }
            };
        case appendMultiLeadApplicationsTypes.reset:
            return {
                ...state,
                [action.id]: {
                    ...currentData,
                    multiLeadApplications: []
                }
            };

        default:
            return state;
    }
}
